export const SUPPORTED_LANGUAGES = [
    { key: "en-US", label: "English", flagCode: "us" },
    { key: "de-DE", label: "Deutsch", flagCode: "de" },
    { key: "nl-NL", label: "Nederlands", flagCode: "nl" },
    { key: "cs-CZ", label: "Čeština", flagCode: "cz" },
    { key: "pl-PL", label: "Polski", flagCode: "pl" },
    { key: "ru-RU", label: "Русский", flagCode: "ru" }
] as const;

export type LanguageKey = typeof SUPPORTED_LANGUAGES[number]["key"];

export const TRANSLATION_KEYS = [
    "app.title",
    "language.title",
    "language.subtitle",
    "batch.title",
    "batch.subtitle",
    "batch.placeholder",
    "batch.error.notFound",
    "batch.error.failed",
    "license.title",
    "license.subtitle",
    "license.placeholder",
    "redirect.title",
    "redirect.message",
    "redirect.quotaTitle",
    "redirect.quotaMessage",
    "redirect.countdown",
    "redirect.confirm",
    "scale.title",
    "scale.message",
    "scale.confirm",
    "success.title",
    "success.message",
    "success.autoReset",
    "actions.continue",
    "actions.back",
    "error.generic"
] as const;

export type TranslationKey = typeof TRANSLATION_KEYS[number];
export type TranslationMap = Record<TranslationKey, string>;

const baseTranslations: TranslationMap = {
    "app.title": "Pre-Check-In",
    "language.title": "Select your language",
    "language.subtitle": "Please choose your preferred language",
    "batch.title": "Transport Batch Number",
    "batch.subtitle": "Please scan or enter the transport batch number",
    "batch.placeholder": "Scan or enter batch number",
    "batch.error.notFound": "Transport batch number not found. Please check and try again.",
    "batch.error.failed": "Failed to fetch transport details. Please try again.",
    "license.title": "License Plate",
    "license.subtitle": "Please confirm or enter the vehicle license plate",
    "license.placeholder": "Enter license plate",
    "redirect.title": "Please visit the gate guard",
    "redirect.message": "Your arrival time is outside the scheduled time window. Please proceed to the gate guard.",
    "redirect.quotaTitle": "Maximum capacity reached",
    "redirect.quotaMessage": "The maximum number of trucks for this transport batch has been reached. Please contact the gate guard.",
    "redirect.countdown": "Resetting in {seconds} seconds...",
    "redirect.confirm": "Proceed to Guard",
    "scale.title": "Proceed to the Scale",
    "scale.message": "Please drive directly to the scale and complete the check-in there.",
    "scale.confirm": "Understood",
    "success.title": "Thank you!",
    "success.message": "Please find a parking spot and wait for the call.",
    "success.autoReset": "Resetting in {seconds} seconds...",
    "actions.continue": "Continue",
    "actions.back": "Back",
    "error.generic": "An error occurred. Please try again."
};

function buildTranslations(overrides: Partial<TranslationMap>): TranslationMap {
    return { ...baseTranslations, ...overrides };
}

export const translations: Record<LanguageKey, TranslationMap> = {
    "en-US": baseTranslations,
    "de-DE": buildTranslations({
        "app.title": "Pre-Check-In",
        "language.title": "Wählen Sie Ihre Sprache",
        "language.subtitle": "Bitte wählen Sie Ihre bevorzugte Sprache",
        "batch.title": "Transportchargennummer",
        "batch.subtitle": "Bitte scannen oder geben Sie die Transportchargennummer ein",
        "batch.placeholder": "Scannen oder Nummer eingeben",
        "batch.error.notFound": "Transportchargennummer nicht gefunden. Bitte überprüfen Sie die Eingabe.",
        "batch.error.failed": "Fehler beim Abrufen der Transportdaten. Bitte versuchen Sie es erneut.",
        "license.title": "Kennzeichen",
        "license.subtitle": "Bitte bestätigen oder geben Sie das Fahrzeugkennzeichen ein",
        "license.placeholder": "Kennzeichen eingeben",
        "redirect.title": "Bitte melden Sie sich beim Pförtner",
        "redirect.message": "Ihre Ankunftszeit liegt außerhalb des gebuchten Zeitfensters. Bitte wenden Sie sich an den Pförtner.",
        "redirect.quotaTitle": "Maximale Kapazität erreicht",
        "redirect.quotaMessage": "Die maximale Anzahl an LKWs für diese Transportcharge wurde erreicht. Bitte wenden Sie sich an den Pförtner.",
        "redirect.countdown": "Zurücksetzen in {seconds} Sekunden...",
        "redirect.confirm": "Zum Pförtner gehen",
        "scale.title": "Bitte fahren Sie zur Waage",
        "scale.message": "Bitte fahren Sie direkt zur Waage und führen Sie den Check-in durch.",
        "scale.confirm": "Verstanden",
        "success.title": "Vielen Dank!",
        "success.message": "Bitte suchen Sie sich einen Parkplatz und warten Sie auf den Abruf.",
        "success.autoReset": "Zurücksetzen in {seconds} Sekunden...",
        "actions.continue": "Weiter",
        "actions.back": "Zurück",
        "error.generic": "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
    }),
    "nl-NL": buildTranslations({
        "app.title": "Pre-Check-In",
        "language.title": "Selecteer uw taal",
        "language.subtitle": "Kies uw voorkeurstaal",
        "batch.title": "Transportbatchnummer",
        "batch.subtitle": "Scan of voer het transportbatchnummer in",
        "batch.placeholder": "Scan of voer nummer in",
        "batch.error.notFound": "Transportbatchnummer niet gevonden. Controleer en probeer opnieuw.",
        "batch.error.failed": "Fout bij het ophalen van transportgegevens. Probeer het opnieuw.",
        "license.title": "Kenteken",
        "license.subtitle": "Bevestig of voer het voertuigkenteken in",
        "license.placeholder": "Voer kenteken in",
        "redirect.title": "Ga naar de poortwachter",
        "redirect.message": "Uw aankomsttijd valt buiten het geplande tijdvenster. Ga naar de poortwachter.",
        "redirect.quotaTitle": "Maximale capaciteit bereikt",
        "redirect.quotaMessage": "Het maximale aantal vrachtwagens voor deze transportbatch is bereikt. Neem contact op met de poortwachter.",
        "redirect.countdown": "Opnieuw instellen over {seconds} seconden...",
        "redirect.confirm": "Naar de Poortwachter",
        "scale.title": "Ga naar de weegbrug",
        "scale.message": "Rijd rechtstreeks naar de weegbrug en voltooi daar de check-in.",
        "scale.confirm": "Begrepen",
        "success.title": "Dank u wel!",
        "success.message": "Zoek een parkeerplaats en wacht op oproep.",
        "success.autoReset": "Opnieuw instellen over {seconds} seconden...",
        "actions.continue": "Doorgaan",
        "actions.back": "Terug",
        "error.generic": "Er is een fout opgetreden. Probeer het opnieuw."
    }),
    "cs-CZ": buildTranslations({
        "app.title": "Pre-Check-In",
        "language.title": "Vyberte svůj jazyk",
        "language.subtitle": "Vyberte si prosím preferovaný jazyk",
        "batch.title": "Číslo přepravní dávky",
        "batch.subtitle": "Naskenujte nebo zadejte číslo přepravní dávky",
        "batch.placeholder": "Naskenujte nebo zadejte číslo",
        "batch.error.notFound": "Číslo přepravní dávky nebylo nalezeno. Zkontrolujte a zkuste to znovu.",
        "batch.error.failed": "Chyba při načítání údajů o přepravě. Zkuste to znovu.",
        "license.title": "Registrační značka",
        "license.subtitle": "Potvrďte nebo zadejte registrační značku vozidla",
        "license.placeholder": "Zadejte registrační značku",
        "redirect.title": "Navštivte vrátnici",
        "redirect.message": "Váš čas příjezdu je mimo plánované časové okno. Jděte k vrátnici.",
        "redirect.quotaTitle": "Maximální kapacita dosažena",
        "redirect.quotaMessage": "Maximální počet nákladních vozidel pro tuto přepravní dávku byl dosažen. Kontaktujte vrátnici.",
        "redirect.countdown": "Resetování za {seconds} sekund...",
        "redirect.confirm": "Jít k vrátnici",
        "scale.title": "Jděte k váze",
        "scale.message": "Jeďte přímo k váze a proveďte tam check-in.",
        "scale.confirm": "Rozumím",
        "success.title": "Děkujeme!",
        "success.message": "Najděte si parkovací místo a čekejte na vyzvání.",
        "success.autoReset": "Resetování za {seconds} sekund...",
        "actions.continue": "Pokračovat",
        "actions.back": "Zpět",
        "error.generic": "Došlo k chybě. Zkuste to znovu."
    }),
    "pl-PL": buildTranslations({
        "app.title": "Pre-Check-In",
        "language.title": "Wybierz język",
        "language.subtitle": "Wybierz preferowany język",
        "batch.title": "Numer partii transportowej",
        "batch.subtitle": "Zeskanuj lub wprowadź numer partii transportowej",
        "batch.placeholder": "Zeskanuj lub wprowadź numer",
        "batch.error.notFound": "Nie znaleziono numeru partii transportowej. Sprawdź i spróbuj ponownie.",
        "batch.error.failed": "Błąd podczas pobierania danych transportu. Spróbuj ponownie.",
        "license.title": "Numer rejestracyjny",
        "license.subtitle": "Potwierdź lub wprowadź numer rejestracyjny pojazdu",
        "license.placeholder": "Wprowadź numer rejestracyjny",
        "redirect.title": "Zgłoś się do portiera",
        "redirect.message": "Twój czas przyjazdu jest poza zaplanowanym oknem czasowym. Udaj się do portiera.",
        "redirect.quotaTitle": "Osiągnięto maksymalną pojemność",
        "redirect.quotaMessage": "Osiągnięto maksymalną liczbę ciężarówek dla tej partii transportowej. Skontaktuj się z portierem.",
        "redirect.countdown": "Resetowanie za {seconds} sekund...",
        "redirect.confirm": "Idź do portiera",
        "scale.title": "Jedź do wagi",
        "scale.message": "Jedź bezpośrednio do wagi i wykonaj tam check-in.",
        "scale.confirm": "Rozumiem",
        "success.title": "Dziękujemy!",
        "success.message": "Znajdź miejsce parkingowe i czekaj na wezwanie.",
        "success.autoReset": "Resetowanie za {seconds} sekund...",
        "actions.continue": "Kontynuuj",
        "actions.back": "Wstecz",
        "error.generic": "Wystąpił błąd. Spróbuj ponownie."
    }),
    "ru-RU": buildTranslations({
        "app.title": "Предварительная регистрация",
        "language.title": "Выберите язык",
        "language.subtitle": "Пожалуйста, выберите предпочитаемый язык",
        "batch.title": "Номер транспортной партии",
        "batch.subtitle": "Отсканируйте или введите номер транспортной партии",
        "batch.placeholder": "Сканируйте или введите номер",
        "batch.error.notFound": "Номер транспортной партии не найден. Проверьте и попробуйте снова.",
        "batch.error.failed": "Ошибка при получении данных о транспорте. Попробуйте снова.",
        "license.title": "Регистрационный номер",
        "license.subtitle": "Подтвердите или введите регистрационный номер транспортного средства",
        "license.placeholder": "Введите регистрационный номер",
        "redirect.title": "Обратитесь к охраннику",
        "redirect.message": "Ваше время прибытия находится вне запланированного временного окна. Обратитесь к охраннику.",
        "redirect.quotaTitle": "Достигнута максимальная вместимость",
        "redirect.quotaMessage": "Достигнуто максимальное количество грузовиков для этой транспортной партии. Обратитесь к охраннику.",
        "redirect.countdown": "Сброс через {seconds} секунд...",
        "redirect.confirm": "К охраннику",
        "scale.title": "Проедьте к весам",
        "scale.message": "Проедьте прямо к весам и выполните там регистрацию.",
        "scale.confirm": "Понятно",
        "success.title": "Спасибо!",
        "success.message": "Найдите парковочное место и ожидайте вызова.",
        "success.autoReset": "Сброс через {seconds} секунд...",
        "actions.continue": "Продолжить",
        "actions.back": "Назад",
        "error.generic": "Произошла ошибка. Попробуйте снова."
    })
};

export function getTranslations(lang: LanguageKey): TranslationMap {
    return translations[lang] || translations["en-US"];
}
